// What the run page keeps on screen after an approval / release action was refused with
// one of the Phase 4 codes. The toast carries the server's words and disappears; this says
// what happens next and offers the one action that helps (Recalculate, Refresh, or go to
// the approvals). It is a warning surface, never a success one — a refusal must not read
// as "all clear".
import React from 'react';
import { AlertTriangle, RefreshCw, RotateCcw, ClipboardCheck, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import type { PayrollActionNotice } from '../lib/payrollActionErrors';

interface RunActionNoticeProps {
  notice: PayrollActionNotice | null;
  busy?: boolean;
  onRecalculate: () => void;
  onRefresh: () => void;
  onGoToApprovals: () => void;
  onDismiss: () => void;
}

export const RunActionNotice: React.FC<RunActionNoticeProps> = ({
  notice,
  busy,
  onRecalculate,
  onRefresh,
  onGoToApprovals,
  onDismiss,
}) => {
  if (!notice) return null;
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg bg-warning-soft p-4 text-sm text-warning-strong">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1 space-y-2">
        <p className="font-medium">{notice.title}</p>
        {notice.detail && notice.detail !== notice.title && (
          <p data-testid="run-action-notice-detail">{notice.detail}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {notice.followUp === 'recalculate' && (
            <Button size="sm" variant="outline" className="gap-2" disabled={busy} onClick={onRecalculate}>
              <RotateCcw className="h-4 w-4" /> Recalculate
            </Button>
          )}
          {notice.followUp === 'wait' && (
            <Button size="sm" variant="outline" className="gap-2" disabled={busy} onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          )}
          {notice.followUp === 'refresh' && (
            <Button size="sm" variant="outline" className="gap-2" disabled={busy} onClick={onGoToApprovals}>
              <ClipboardCheck className="h-4 w-4" /> Review approvals
            </Button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded p-1 hover:bg-background/40"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
